import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { 
  Brain, 
  Mail, 
  Lock, 
  User, 
  ArrowRight, 
  AlertCircle, 
  Sparkles,
  Eye,
  EyeOff,
  Check,
  BarChart3,
  Zap
} from 'lucide-react';
import './IntelligenceAuth.css';
import { authDestination } from '../lib/authDestination';
import { CrescentBrand } from '../components/CrescentBrand';

const IntelligenceAuth = () => {
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(new URLSearchParams(location.search).get('signup') !== '1');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(location.state?.error || '');
  const [loading, setLoading] = useState(false);
  const { login, signup, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const destination = authDestination(location.search);
  useEffect(() => { if (!authLoading && isAuthenticated) navigate(destination, { replace: true }); }, [authLoading, isAuthenticated, navigate, destination]);

  const handleGoogleSignIn = () => {
    sessionStorage.setItem('crescent-auth-return', destination);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/intelligence';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
      } else {
        await signup(formData.email, formData.password, formData.name);
      }
      navigate(destination, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setError('');
  };

  return (
    <div className="intelligence-auth-page" data-testid="auth-page">
      <div className="auth-background">
        <div className="auth-gradient-1" />
        <div className="auth-gradient-2" />
        <div className="auth-grid" />
      </div>

      <motion.div 
        className="auth-container"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Logo/Brand */}
        <CrescentBrand intelligence testId="auth-advisory-home" />

        {/* Auth Card */}
        <motion.div 
          className="auth-card"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <div className="auth-header">
            <h1>{isLogin ? 'Welcome Back' : 'Create Account'}</h1>
            <p>{isLogin ? 'Sign in to access your intelligence console' : 'Start your financial education journey'}</p>
          </div>

          {error && (
            <motion.div 
              className="auth-error"
              role="alert"
              data-testid="auth-error"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <AlertCircle size={16} />
              <span>{error}</span>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="auth-form">
            {!isLogin && (
              <div className="form-group">
                <label htmlFor="name">
                  <User size={16} />
                  <span>Full Name</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="John Doe"
                  required={!isLogin}
                  data-testid="name-input"
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">
                <Mail size={16} />
                <span>Email Address</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
                data-testid="email-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">
                <Lock size={16} />
                <span>Password</span>
              </label>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button 
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="password-toggle"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button 
              type="submit" 
              className="auth-submit-btn"
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <span className="loading-spinner" />
              ) : (
                <>
                  <span>{isLogin ? 'Sign In' : 'Create Account'}</span>
                  <ArrowRight size={18} />
                </>
              )}
            </button>
          </form>

          <div className="auth-divider">
            <span>or</span>
          </div>

          <button
            type="button"
            className="google-auth-btn"
            onClick={handleGoogleSignIn}
            data-testid="google-signin-btn"
          >
            <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
              <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
              <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
              <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            </svg>
            <span>Continue with Google</span>
          </button>

          <div className="auth-divider auth-divider-tight">
            <span>{isLogin ? "New here?" : "Have an account?"}</span>
          </div>

          <div className="auth-switch-section">
            <p className="switch-text">
              {isLogin ? "New to Crescent Intelligence?" : "Already have an account?"}
            </p>
            <button 
              className="auth-toggle-btn"
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
              }}
              data-testid="toggle-auth-mode"
            >
              {isLogin ? "Create your free account" : "Sign in instead"}
            </button>
          </div>

          {/* Features */}
          <div className="auth-features">
            <div className="features-header">Free plan includes:</div>
            <div className="features-list">
              <div className="feature-item">
                <Check size={14} />
                <span>3 AI queries per day</span>
              </div>
              <div className="feature-item">
                <Check size={14} />
                <span>Financial intelligence insights</span>
              </div>
              <div className="feature-item">
                <Check size={14} />
                <span>Access to analysis tools</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Back to main site */}
        <Link to="/" className="back-link" data-testid="auth-back-home">
          ← Back to Crescent Advisory
        </Link>
      </motion.div>
    </div>
  );
};

export default IntelligenceAuth;
